import type { ImportTableColumn } from "../../../../utilities/types";
import { Job, type Contact } from "../../../../models";
import { columnDisplayNames } from "../../../admin/ImportSettings";
import { FIELD_TO_PROPERTY_MAP } from "../../../../utilities/jobValidation";
import { CSV } from "@rogoag/shared-ts";
import dayjs from "dayjs";

// ============= CONSTANTS =============

export const CSV_ID_COLUMN = "__id";

/**
 * Ordered list of column keys that can round-trip through CSV.
 * Excludes file columns (boundaryGeoJSON, pointsGeoJSON, zonesGeoJSON, fieldNotes)
 * and lookup columns (deal, labName, branch, microsList) that can't round-trip.
 * Order matches columnDisplayNames grouping.
 */
export const CSV_COLUMN_KEYS: ImportTableColumn[] = [
	// Basic Information
	"grower",
	"farm",
	"field",
	// Order
	"isReadyToSample",
	"dateFieldReadyEstimated",
	"fieldPriority",
	"collectBoundary",
	"submissionNotes",
	// Deal
	"density",
	"depth",
	"testPackage",
	"frequencyOfMicros",
	// Lab Info
	"labInstructions",
	"lastCrop",
	"nextCrop",
	"yieldGoal",
	// Contacts
	"salesperson",
	"salespersonMobilePhone",
	"growerPhoneNumber",
	"submitterName",
	"submitterEmail",
	// Sync Codes
	"eventId",
	"labSubmissionCode",
	"fieldId",
	"externalId",
	"externalFarmId",
	"externalLabId",
	// Misc
	"isTestJob",
];

// ============= TYPES =============

export interface CSVImportResult {
	jobs: Job[];
	warnings: string[];
	errors: string[];
}

export interface CSVImportSummary {
	updated: number;
	added: number;
	removed: number;
	warnings: string[];
	/** Display names of columns that have at least one changed value across updated jobs */
	changedColumns: string[];
}

// ============= HELPERS =============

/**
 * Build a reverse lookup from display name → column key.
 */
function buildDisplayNameToKeyMap(): Map<string, ImportTableColumn> {
	const map = new Map<string, ImportTableColumn>();
	for (const key of CSV_COLUMN_KEYS) {
		map.set(columnDisplayNames[key], key);
	}
	return map;
}

/**
 * Get the job property value for a given column key.
 */
function getJobValue(job: Job, key: ImportTableColumn, salespersons: Contact[]): string {
	const prop = FIELD_TO_PROPERTY_MAP[key];

	switch (key) {
		case "isReadyToSample":
			return job.isReadyToSample ? "Yes" : "No";
		case "collectBoundary":
			return job.collectBoundary ? "Yes" : "No";
		case "isTestJob":
			return job.isTestJob ? "Yes" : "No";

		case "density":
			return job.importDensity?.toString() || "";

		case "depth":
			return job.importDepth?.toString() || "";

		case "frequencyOfMicros":
			return job.externalFrequencyOfMicros?.toString() || "";

		case "testPackage":
			return job.externalTestPackageId || "";

		case "fieldPriority":
			return job.fieldPriority || "Regular Turn (default)";

		case "salesperson":
			return (job.salespersonIds || [])
				.map((id) => salespersons.find((sp) => sp.id === id)?.name || "")
				.filter((name) => name)
				.join("; ");

		case "salespersonMobilePhone":
			return job.newSalesperson?.phone || "";

		case "dateFieldReadyEstimated":
			return job.dateFieldReadyEstimated ? job.dateFieldReadyEstimated.toLocaleDateString() : "";

		case "grower":
			return job.importGrowerName || "";
		case "farm":
			return job.importFarmName || "";
		case "field":
			return job.importFieldName || "";

		default: {
			// For simple string fields, use the property map
			const value = (job as any)[prop];
			return value?.toString() || "";
		}
	}
}

// ============= EXPORTED FUNCTIONS =============

/**
 * Build CSV header row. Always starts with __id, then display names for each column key.
 * If visibleColumns is provided, only includes columns in that set.
 */
export function buildCSVHeaders(visibleColumns?: Set<ImportTableColumn>): string[] {
	const headers = [CSV_ID_COLUMN];
	for (const key of CSV_COLUMN_KEYS) {
		if (visibleColumns && !visibleColumns.has(key)) continue;
		headers.push(columnDisplayNames[key]);
	}
	return headers;
}

/**
 * Convert a single job to a CSV row array.
 * First element is job.id, then values for each column key.
 */
export function jobToCSVRow(job: Job, salespersons: Contact[], visibleColumns?: Set<ImportTableColumn>): string[] {
	const row = [job.id];
	for (const key of CSV_COLUMN_KEYS) {
		if (visibleColumns && !visibleColumns.has(key)) continue;
		row.push(getJobValue(job, key, salespersons));
	}
	return row;
}

/**
 * Build a complete CSV string from jobs array.
 * Uses CSV.from2DArray for proper escaping.
 */
export function buildCSVString(jobs: Job[], salespersons: Contact[], visibleColumns?: Set<ImportTableColumn>): string {
	const headers = buildCSVHeaders(visibleColumns);
	const rows = jobs.map((job) => jobToCSVRow(job, salespersons, visibleColumns));
	return CSV.from2DArray([headers, ...rows]);
}

/**
 * Apply a CSV row's values to a job, handling type coercion.
 * Returns an array of warning messages for any parse issues.
 */
export function applyCSVRowToJob(
	row: Record<string, string>,
	job: Job,
	displayNameToKey: Map<string, ImportTableColumn>,
	salespersons: Contact[],
): string[] {
	const warnings: string[] = [];

	for (const [displayName, value] of Object.entries(row)) {
		if (displayName === CSV_ID_COLUMN) continue;

		const key = displayNameToKey.get(displayName);
		if (!key) continue; // Unknown column, skip (warning added at parse level)

		switch (key) {
			case "density": {
				const num = parseFloat(value);
				if (value !== "" && isNaN(num)) {
					warnings.push(`Invalid density "${value}" — kept previous value`);
				} else if (value === "") {
					job.importDensity = undefined;
				} else {
					job.importDensity = num;
				}
				break;
			}
			case "depth": {
				const num = parseFloat(value);
				if (value !== "" && isNaN(num)) {
					warnings.push(`Invalid depth "${value}" — kept previous value`);
				} else if (value === "") {
					job.importDepth = undefined;
				} else {
					job.importDepth = num;
				}
				break;
			}
			case "frequencyOfMicros": {
				const num = parseFloat(value);
				if (value !== "" && isNaN(num)) {
					warnings.push(`Invalid frequency of micros "${value}" — kept previous value`);
				} else if (value === "") {
					job.externalFrequencyOfMicros = undefined;
				} else {
					job.externalFrequencyOfMicros = num;
				}
				break;
			}
			case "isReadyToSample": {
				const lower = value.toLowerCase().trim();
				job.isReadyToSample = lower === "yes" || lower === "true" || lower === "1";
				break;
			}
			case "dateFieldReadyEstimated": {
				if (!value.trim()) {
					job.dateFieldReadyEstimated = undefined;
					break;
				}
				const parsed = dayjs(value);
				if (parsed.isValid()) {
					job.dateFieldReadyEstimated = parsed.toDate();
				} else {
					warnings.push(`Invalid date "${value}" — kept previous value`);
				}
				break;
			}
			case "salesperson": {
				if (!value.trim()) {
					job.salespersonIds = [];
					break;
				}
				const names = value
					.split(";")
					.map((n) => n.trim())
					.filter(Boolean);
				const ids: string[] = [];
				for (const name of names) {
					const sp = salespersons.find((s) => s.name === name);
					if (sp) {
						ids.push(sp.id);
					} else {
						warnings.push(`Unknown salesperson "${name}" — skipped`);
					}
				}
				job.salespersonIds = ids;
				break;
			}
			case "grower":
				job.importGrowerName = value;
				break;
			case "farm":
				job.importFarmName = value;
				break;
			case "field":
				job.importFieldName = value;
				break;
			case "testPackage":
				job.externalTestPackageId = value;
				break;
			case "fieldPriority":
				job.fieldPriority = value as Job["fieldPriority"];
				break;
			case "lastCrop":
				job.lastCrop = value || undefined;
				break;
			case "nextCrop":
				job.nextCrop = value || undefined;
				break;
			case "yieldGoal":
				job.yieldGoal = value;
				break;
			case "labSubmissionCode":
				job.labSubmissionCode = value;
				break;
			case "labInstructions":
				job.labInstructions = value;
				break;
			case "growerPhoneNumber":
				job.growerPhoneNumber = value;
				break;
			case "submissionNotes":
				job.submissionNotes = value;
				break;
			case "fieldId":
				job.fieldId = value;
				break;
			case "submitterName":
				job.submitterName = value;
				break;
			case "submitterEmail":
				job.submitterEmail = value;
				break;
			case "collectBoundary": {
				const lower = value.toLowerCase().trim();
				job.collectBoundary = lower === "yes" || lower === "true" || lower === "1";
				break;
			}
			case "isTestJob": {
				const lower = value.toLowerCase().trim();
				job.isTestJob = lower === "yes" || lower === "true" || lower === "1";
				break;
			}
			case "salespersonMobilePhone":
				if (value.trim()) {
					job.newSalesperson = { ...job.newSalesperson, name: job.newSalesperson?.name || "", phone: value };
				} else if (job.newSalesperson) {
					job.newSalesperson.phone = "";
				}
				break;
			case "eventId":
				job.eventId = value;
				break;
			case "externalId":
				job.externalId = value;
				break;
			case "externalFarmId":
				job.externalFarmId = value;
				break;
			case "externalLabId":
				job.externalLabId = value;
				break;
		}
	}

	return warnings;
}

/**
 * Parse a CSV string and produce a new set of jobs.
 * - Rows with matching __id update existing jobs (preserving files, GeoJSON, etc.)
 * - Rows without __id (or unrecognized __id) create new jobs
 * - Existing jobs not in CSV are excluded from the result (treated as deleted)
 */
export function parseCSVToJobs(csvText: string, existingJobs: Job[], salespersons: Contact[]): CSVImportResult {
	const warnings: string[] = [];
	const errors: string[] = [];

	if (!csvText.trim()) {
		return { jobs: [], warnings, errors: ["CSV file is empty"] };
	}

	// Parse CSV to JSON objects (keyed by header name)
	const rows = CSV.toJSON(csvText);

	if (rows.length === 0) {
		// Check if we at least had headers
		const lines = csvText.trim().split("\n");
		if (lines.length <= 1) {
			// Only headers, no data rows — return empty jobs with no error
			return { jobs: [], warnings, errors: [] };
		}
		return { jobs: [], warnings, errors: [] };
	}

	// Check for recognized headers
	const displayNameToKey = buildDisplayNameToKeyMap();
	const firstRowKeys = Object.keys(rows[0]);
	const hasIdColumn = firstRowKeys.includes(CSV_ID_COLUMN);

	// Check for unknown columns
	const unknownHeaders: string[] = [];
	for (const header of firstRowKeys) {
		if (header === CSV_ID_COLUMN) continue;
		if (!displayNameToKey.has(header)) {
			unknownHeaders.push(header);
		}
	}
	if (unknownHeaders.length > 0) {
		return {
			jobs: [],
			warnings,
			errors: [
				`Unrecognized column(s): ${unknownHeaders.join(", ")}`,
				`Expected columns: ${Array.from(displayNameToKey.keys()).join(", ")}`,
			],
		};
	}

	// Build lookup for existing jobs by ID
	const existingJobMap = new Map(existingJobs.map((j) => [j.id, j]));

	// Process rows
	const resultJobs: Job[] = [];

	for (const row of rows) {
		const rowId = hasIdColumn ? row[CSV_ID_COLUMN]?.trim() : undefined;
		const existingJob = rowId ? existingJobMap.get(rowId) : undefined;

		let job: Job;
		if (existingJob) {
			// Update existing job — deep clone to avoid mutation
			job = JSON.parse(JSON.stringify(existingJob));
			// Restore Date objects that JSON serialization converts to strings
			if (existingJob.dateFieldReadyEstimated) {
				job.dateFieldReadyEstimated = new Date(existingJob.dateFieldReadyEstimated);
			}
			if (existingJob.dateCreated) {
				job.dateCreated = new Date(existingJob.dateCreated);
			}
		} else {
			// New job
			job = Job.defaultForImport();
		}

		const rowWarnings = applyCSVRowToJob(row, job, displayNameToKey, salespersons);
		warnings.push(...rowWarnings);
		resultJobs.push(job);
	}

	return { jobs: resultJobs, warnings, errors };
}

/**
 * Compute a summary of what an import will do compared to existing jobs.
 */
export function computeImportSummary(
	result: CSVImportResult,
	existingJobs: Job[],
	salespersons: Contact[],
): CSVImportSummary {
	const existingJobMap = new Map(existingJobs.map((j) => [j.id, j]));
	const resultIds = new Set(result.jobs.map((j) => j.id));

	let updated = 0;
	let added = 0;
	const changedColumnKeys = new Set<ImportTableColumn>();

	for (const job of result.jobs) {
		const existing = existingJobMap.get(job.id);
		if (existing) {
			updated++;
			// Compare each column value to detect changes
			for (const key of CSV_COLUMN_KEYS) {
				if (changedColumnKeys.has(key)) continue; // Already detected
				const oldVal = getJobValue(existing, key, salespersons);
				const newVal = getJobValue(job, key, salespersons);
				if (oldVal !== newVal) {
					changedColumnKeys.add(key);
				}
			}
		} else {
			added++;
		}
	}

	const removed = existingJobs.filter((j) => !resultIds.has(j.id)).length;

	// Convert column keys to display names, preserving CSV_COLUMN_KEYS order
	const changedColumns = CSV_COLUMN_KEYS.filter((key) => changedColumnKeys.has(key)).map(
		(key) => columnDisplayNames[key],
	);

	return {
		updated,
		added,
		removed,
		warnings: result.warnings,
		changedColumns,
	};
}
